import { supabase } from '../lib/supabase';

export const rbacService = {
  // Custom Roles
  async getAllRoles() {
    const { data, error } = await supabase?.from('custom_roles')?.select('*')?.order('role_name', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      roleName: row?.role_name,
      roleDescription: row?.role_description,
      isSystemRole: row?.is_system_role,
      isActive: row?.is_active,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async createRole(role) {
    const { data, error } = await supabase?.from('custom_roles')?.insert({
        role_name: role?.roleName,
        role_description: role?.roleDescription,
        is_system_role: false,
        created_by: role?.createdBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      roleName: data?.role_name,
      roleDescription: data?.role_description,
      isSystemRole: data?.is_system_role,
      isActive: data?.is_active,
      createdAt: data?.created_at
    };
  },

  async updateRole(roleId, updates) {
    const { data, error } = await supabase?.from('custom_roles')?.update({
        role_name: updates?.roleName,
        role_description: updates?.roleDescription,
        is_active: updates?.isActive,
        updated_at: new Date()?.toISOString()
      })?.eq('id', roleId)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      roleName: data?.role_name,
      roleDescription: data?.role_description,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  async deleteRole(roleId) {
    const { error } = await supabase?.from('custom_roles')?.delete()?.eq('id', roleId)?.eq('is_system_role', false);
    
    if (error) throw error;
  },

  // Role Permissions
  async getRolePermissions(roleId) {
    const { data, error } = await supabase?.from('role_permissions')?.select('*')?.eq('role_id', roleId);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      roleId: row?.role_id,
      module: row?.module,
      action: row?.action,
      scope: row?.scope,
      allowedDepartments: row?.allowed_departments || [],
      createdAt: row?.created_at
    })) || [];
  },

  async setRolePermissions(roleId, permissions) {
    // Delete existing permissions
    await supabase?.from('role_permissions')?.delete()?.eq('role_id', roleId);
    
    // Insert new permissions
    const permissionsToInsert = permissions?.map(p => ({
      role_id: roleId,
      module: p?.module,
      action: p?.action,
      scope: p?.scope,
      allowed_departments: p?.allowedDepartments || []
    }));
    
    const { data, error } = await supabase?.from('role_permissions')?.insert(permissionsToInsert)?.select();
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      roleId: row?.role_id,
      module: row?.module,
      action: row?.action,
      scope: row?.scope,
      allowedDepartments: row?.allowed_departments || []
    })) || [];
  },

  // User Role Assignments
  async getUserRoleAssignments(userEmail) {
    const { data, error } = await supabase?.from('user_role_assignments')?.select(`
        *,
        custom_roles (
          role_name,
          role_description
        )
      `)?.eq('user_email', userEmail)?.eq('is_active', true);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      userEmail: row?.user_email,
      roleId: row?.role_id,
      roleName: row?.custom_roles?.role_name,
      roleDescription: row?.custom_roles?.role_description,
      department: row?.department,
      assignedBy: row?.assigned_by,
      assignedAt: row?.assigned_at,
      isActive: row?.is_active
    })) || [];
  },

  async assignRoleToUser(assignment) {
    const { data, error } = await supabase?.from('user_role_assignments')?.insert({
        user_email: assignment?.userEmail,
        role_id: assignment?.roleId,
        department: assignment?.department,
        assigned_by: assignment?.assignedBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      userEmail: data?.user_email,
      roleId: data?.role_id,
      department: data?.department,
      assignedAt: data?.assigned_at
    };
  },

  async revokeUserRole(assignmentId) {
    const { error } = await supabase?.from('user_role_assignments')?.update({ is_active: false })?.eq('id', assignmentId);
    
    if (error) throw error;
  },

  // Permission Checks
  async checkUserPermission(userEmail, module, action, department = null) {
    const { data, error } = await supabase?.rpc('user_has_permission', {
        p_user_email: userEmail,
        p_module: module,
        p_action: action,
        p_department: department
      });
    
    if (error) throw error;
    
    return data || false;
  },

  async getUserAccessibleDepartments(userEmail, module) {
    const { data, error } = await supabase?.rpc('get_user_accessible_departments', {
        p_user_email: userEmail,
        p_module: module
      });
    
    if (error) throw error;
    
    return data || [];
  },

  // Department Access Log
  async logDepartmentAccess(log) {
    const { error } = await supabase?.from('department_access_log')?.insert({
        user_email: log?.userEmail,
        department: log?.department,
        module: log?.module,
        action: log?.action,
        access_granted: log?.accessGranted,
        denial_reason: log?.denialReason
      });
    
    if (error) throw error;
  },

  async getDepartmentAccessLog(filters = {}) {
    let query = supabase?.from('department_access_log')?.select('*')?.order('accessed_at', { ascending: false });
    
    if (filters?.userEmail) {
      query = query?.eq('user_email', filters?.userEmail);
    }
    
    if (filters?.department) {
      query = query?.eq('department', filters?.department);
    }
    
    if (filters?.module) {
      query = query?.eq('module', filters?.module);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      userEmail: row?.user_email,
      department: row?.department,
      module: row?.module,
      action: row?.action,
      accessGranted: row?.access_granted,
      denialReason: row?.denial_reason,
      accessedAt: row?.accessed_at
    })) || [];
  }
};